import React, { useState, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Settings, Save, Cancel, Send, ExpandMore, Email as EmailIcon, Refresh, CheckCircle, Delete, HourglassEmpty } from '@mui/icons-material';
import { Logger } from '../../../utils/Logger';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
// import { FlexBetween } from '../../../components/FlexBetween';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { EmailConfig, EmailProviderType } from '../../Interfaces/Company/Company';
import { apiClient } from '../../../utils/ApiClient';
// import { API_BASE_URL } from '../../../envConfig';

export const Email: React.FC = () => {
  const theme = useTheme();
  const panelIconColor = theme.palette.heading?.main ?? theme.palette.secondary.main;
  const user = useSelector((state: RootState) => state.authentication.user);
  const { hasInstantUpdateAccess, getInstantUpdateRole, isUserSuperAdmin } = useModulePermissions();

  const [configuration, setConfiguration] = useState<EmailConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [testing, setTesting] = useState<boolean>(false);
  const [customSenderExpanded, setCustomSenderExpanded] = useState<boolean>(false);
  const [customSenderEmail, setCustomSenderEmail] = useState<string>('');
  const [customSenderEmailError, setCustomSenderEmailError] = useState<string>('');
  const [submittingVerification, setSubmittingVerification] = useState<boolean>(false);
  const [checkingStatus, setCheckingStatus] = useState<boolean>(false);
  const [deletingIdentity, setDeletingIdentity] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [cancellingRequest, setCancellingRequest] = useState<boolean>(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState<boolean>(false);

  // Derive requested identity status from configuration
  const requestedIdentity = (configuration as any)?.ses?.requestedIdentity;
  const hasPendingIdentity = requestedIdentity?.email && requestedIdentity?.status === 'pending';
  const hasVerifiedIdentity = requestedIdentity?.email && requestedIdentity?.status === 'verified';

  // Function to check identity verification status
  const handleCheckIdentityStatus = async () => {
    try {
      setCheckingStatus(true);
      const response = await apiClient.get('/configuration/email/identity-status');
      const data = response.data;
      
      if (data?.status === 'verified') {
        setSnackbar({ 
          open: true, 
          message: 'Email identity verified successfully! It is now set as the default sender.', 
          severity: 'success' 
        });
      } else if (data?.status === 'pending') {
        setSnackbar({ 
          open: true, 
          message: 'Verification is still pending. Please check your inbox and click the verification link.', 
          severity: 'info' as any 
        });
      } else if (data?.status === 'failed') {
        setSnackbar({ 
          open: true, 
          message: 'Verification failed. Please submit a new request.', 
          severity: 'error' 
        });
      }
      // Refresh configuration to get updated status
      fetchConfiguration();
    } catch (error: any) {
      Logger.error("Email.tsx : handleCheckIdentityStatus()", `Failed: ${error}`);
      setSnackbar({ 
        open: true, 
        message: error?.message || 'Failed to check verification status.', 
        severity: 'error' 
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  // Function to delete email identity
  const handleDeleteIdentity = async () => {
    try {
      setDeletingIdentity(true);
      await apiClient.delete('/configuration/email/identity');
      setDeleteConfirmOpen(false);
      setSnackbar({ 
        open: true, 
        message: 'Email identity deleted successfully. Default sender email has been restored.', 
        severity: 'success' 
      });
      // Refresh configuration to reflect the changes
      fetchConfiguration();
    } catch (error: any) {
      Logger.error("Email.tsx : handleDeleteIdentity()", `Failed: ${error}`);
      setSnackbar({ 
        open: true, 
        message: error?.message || 'Failed to delete email identity.', 
        severity: 'error' 
      });
    } finally {
      setDeletingIdentity(false);
    }
  };

  // Function to cancel a pending email identity verification request
  const handleCancelIdentityRequest = async () => {
    try {
      setCancellingRequest(true);
      await apiClient.post('/configuration/email/cancel-verification', {});
      setCancelConfirmOpen(false);
      setSnackbar({
        open: true,
        message: 'Verification request cancelled successfully. Default sender email has been restored.',
        severity: 'success'
      });
      // Refresh configuration to reflect the cleared request
      fetchConfiguration();
    } catch (error: any) {
      Logger.error("Email.tsx : handleCancelIdentityRequest()", `Failed: ${error}`);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to cancel the verification request.',
        severity: 'error'
      });
    } finally {
      setCancellingRequest(false);
    }
  };

  const channelName = "Email";
  const userRole = getInstantUpdateRole();
  const canEdit = isUserSuperAdmin || userRole === 'admin';
  const canView = hasInstantUpdateAccess() || isUserSuperAdmin;

  // Check if user has access to this module
  if (!canView) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary">
          You don't have permission to access Email configuration.
        </Typography>
      </Box>
    );
  }

  // Function to fetch configuration
  const fetchConfiguration = async () => {
    if (!user?.companyId) {
      Logger.warn("src/modules/Settings/Email/Email.tsx : fetchConfiguration()", "User companyId is not available.");
      return;
    }
    setLoading(true);
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/configuration/${user.companyId}/email`);
      // Backend now returns data directly in the data field
      const configData = data.data || null;
      setConfiguration(configData);
      Logger.info("src/modules/Settings/Email/Email.tsx : fetchConfiguration()", `Fetched configuration: ${JSON.stringify(configData)}`);
    } catch (error) {
      Logger.error("src/modules/Settings/Email/Email.tsx : fetchConfiguration()", `Failed to fetch configuration: ${error}`);
      setSnackbar({ open: true, message: `Failed to load configuration: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, [user?.companyId]);

  const handleOpenDialog = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    fetchConfiguration();
  };

  const handleSaveConfiguration = async (configData: EmailConfig) => {
    if (!user?.companyId || !canEdit) return;

    try {
      setLoading(true);
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.put(`/configuration/${user.companyId}/email`, configData);
      setSnackbar({ open: true, message: 'Email configuration saved successfully!', severity: 'success' });
      handleCloseDialog();
    } catch (error) {
      Logger.error("src/modules/Settings/Email/Email.tsx : handleSaveConfiguration()", `Failed to save configuration: ${error}`);
      setSnackbar({ open: true, message: `Operation failed: ${error instanceof Error ? error.message : String(error)}`, severity: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTestConfiguration = async () => {
    try {
      setTesting(true);
      const resp = await apiClient.post('/configuration/email/test');
      const ok = (resp as any)?.status === 200;
      setSnackbar({ open: true, message: ok ? 'Test email sent successfully' : 'Test failed', severity: ok ? 'success' : 'error' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Test failed', severity: 'error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Mailing Server Configurations
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={3} sx={{ maxWidth: 800, margin: 'auto' }}>
        <Box sx={{ width: '100%' }}>
          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  textAlign: { xs: 'center', sm: 'left' },
                  color: theme.palette.heading?.main ?? theme.palette.secondary.main,
                }}
              >
                Live Configurations
              </Typography>

              {configuration ? (
                <Box display="flex" flexWrap="wrap" gap={3}>
                  {(configuration.providerType === 'ses' || !configuration.providerType) && (
                    <>
                      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <TextField
                          label="Default Sender Email"
                          value={configuration.ses?.defaultSenderEmail || configuration.awsSESDefaultSenderEmail || 'Not Set'}
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <TextField
                          label="Test Recipient Email"
                          value={configuration.testRecipientEmail || 'Not Set'}
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </>
                  )}
                  {configuration.providerType === 'office365' && (
                    <>
                      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <TextField
                          label="Office365 From Address"
                          value={configuration.office365?.smtp?.fromAddress || 'Not Set'}
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <TextField
                          label="Office365 SMTP User"
                          value={configuration.office365?.smtp?.user || 'Not Set'}
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <TextField
                          label="Test Recipient Email"
                          value={configuration.testRecipientEmail || 'Not Set'}
                          fullWidth
                          margin="normal"
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 5 }}>
                  No {channelName} configuration found. {canEdit ? 'Click "Setup your own mailing server" to get started.' : 'Contact your administrator to configure Email.'}
                </Typography>
              )}

              {canEdit && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Settings />}
                      onClick={handleOpenDialog}
                    >
                      Setup your own mailing server
                    </Button>
                    <LoadingButton
                      variant="contained"
                      color="secondary"
                      startIcon={<Send />}
                      onClick={handleTestConfiguration}
                      loading={testing}
                      loadingPosition="start"
                      disabled={!configuration || !configuration.testRecipientEmail}
                    >
                      {testing ? 'Sending…' : 'Send Test Email'}
                    </LoadingButton>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Verified Identity Panel - shown when identity is verified */}
      {hasVerifiedIdentity && (
        <Box sx={{ maxWidth: 800, margin: 'auto', mt: 3 }}>
          <Card sx={{ borderRadius: '12px', boxShadow: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircle color="success" />
                <Typography variant="h6" color="success.main" fontWeight={500}>
                  Verified Email Identity
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Your email address has been verified and is now set as the default sender for all outgoing emails.
              </Typography>
              
              <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <TextField
                  label="Verified Email Address"
                  value={requestedIdentity?.email || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1, maxWidth: 400 }}
                />
                <Typography variant="caption" color="textSecondary">
                  Verified on: {requestedIdentity?.verifiedAt ? new Date(requestedIdentity.verifiedAt).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Delete Identity
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Custom Sender Email Panel - shown when no verified identity */}
      {!hasVerifiedIdentity && (
        <Box sx={{ maxWidth: 800, margin: 'auto', mt: 3 }}>
          <Accordion 
            expanded={customSenderExpanded} 
            onChange={() => setCustomSenderExpanded(!customSenderExpanded)}
            sx={{ 
              borderRadius: '12px !important',
              boxShadow: 3,
              '&:before': { display: 'none' },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon sx={{ color: panelIconColor }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Want to send emails using your own email address?
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {/* Show pending state if there's a pending identity request */}
              {hasPendingIdentity ? (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <HourglassEmpty color="warning" />
                    <Typography variant="subtitle1" color="warning.main" fontWeight={500}>
                      Verification Pending
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    A verification email has been sent to the address below. Please check your inbox (and spam folder) 
                    and click the verification link to complete the setup. Once verified, this email will become your 
                    default sender address.
                  </Typography>
                  
                  <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
                    <TextField
                      label="Requested Email Address"
                      value={requestedIdentity?.email || ''}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      size="small"
                      sx={{ flexGrow: 1, maxWidth: 400 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Requested: {requestedIdentity?.requestedAt ? new Date(requestedIdentity.requestedAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                    <LoadingButton
                      variant="contained"
                      color="secondary"
                      startIcon={<Refresh />}
                      onClick={handleCheckIdentityStatus}
                      loading={checkingStatus}
                      loadingPosition="start"
                    >
                      {checkingStatus ? 'Checking…' : 'Refresh Status'}
                    </LoadingButton>
                    <LoadingButton
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setCancelConfirmOpen(true)}
                      loading={cancellingRequest}
                      loadingPosition="start"
                      disabled={checkingStatus}
                    >
                      Cancel Request
                    </LoadingButton>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    You can configure our platform to send emails from your own email address without sharing any credentials with us. 
                    Here's how it works: enter your email address below and submit. Amazon Simple Email Service (SES) will then send 
                    a verification email to that address. Once you confirm via the verification link, your email will be approved as 
                    the default sender. The "Default Sender Email" field above will be updated accordingly, and all outgoing emails 
                    from our platform will be sent using your verified email address.
                  </Typography>
                  
                  <Box 
                    component="form" 
                    onSubmit={async (e: React.FormEvent) => {
                      e.preventDefault();
                      // Validate email format
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!customSenderEmail.trim()) {
                        setCustomSenderEmailError('Email address is required');
                        return;
                      }
                      if (!emailRegex.test(customSenderEmail)) {
                        setCustomSenderEmailError('Please enter a valid email address');
                        return;
                      }
                      setCustomSenderEmailError('');
                      
                      // Call API to request email identity verification
                      try {
                        setSubmittingVerification(true);
                        await apiClient.post('/configuration/email/verify-identity', { email: customSenderEmail });
                        setSnackbar({ 
                          open: true, 
                          message: 'Verification email has been sent! Please check your inbox and click the verification link.', 
                          severity: 'success' 
                        });
                        setCustomSenderEmail('');
                        setCustomSenderExpanded(false);
                        // Refresh configuration to show the pending request
                        fetchConfiguration();
                      } catch (error: any) {
                        Logger.error("Email.tsx : requestEmailIdentityVerification()", `Failed: ${error}`);
                        setSnackbar({ 
                          open: true, 
                          message: error?.message || 'Failed to request email verification. Please try again.', 
                          severity: 'error' 
                        });
                      } finally {
                        setSubmittingVerification(false);
                      }
                    }}
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}
                  >
                    <TextField
                      label="Your Email Address"
                      type="email"
                      value={customSenderEmail}
                      onChange={(e) => {
                        setCustomSenderEmail(e.target.value);
                        if (customSenderEmailError) setCustomSenderEmailError('');
                      }}
                      error={!!customSenderEmailError}
                      helperText={customSenderEmailError}
                      placeholder="your-email@example.com"
                      variant="outlined"
                      size="small"
                      sx={{ flexGrow: 1, minWidth: 280 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      startIcon={<Send />}
                      disabled={submittingVerification}
                    >
                      {submittingVerification ? 'Submitting…' : 'Submit for Verification'}
                    </Button>
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Configuration Dialog */}
      {canEdit && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>{configuration ? `Edit ${channelName} Configuration` : `Add New ${channelName} Configuration`}</DialogTitle>
          <DialogContent>
            <ConfigurationForm
              configuration={configuration}
              onSave={handleSaveConfiguration}
              onCancel={handleCloseDialog}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined"
              color="secondary" 
              onClick={handleCloseDialog} 
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => document.getElementById('email-configuration-form')?.dispatchEvent(new Event('submit', { bubbles: true }))}
              startIcon={<Save />} 
              variant="contained" 
              color="success"
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Cancel Verification Request Confirm Dialog */}
      <Dialog open={cancelConfirmOpen} onClose={() => !cancellingRequest && setCancelConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Verification Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            Are you sure you want to cancel the pending verification request for "{requestedIdentity?.email}"?
            This will remove the request from AWS SES and restore the default sender email. You can submit a new request later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            disabled={cancellingRequest}
            color="secondary"
            variant="outlined"
          >
            Keep Request
          </Button>
          <Button
            onClick={handleCancelIdentityRequest}
            color="error"
            variant="contained"
            disabled={cancellingRequest}
            startIcon={cancellingRequest ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {cancellingRequest ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Identity Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deletingIdentity && setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            Are you sure you want to delete the email identity "{requestedIdentity?.email}"? 
            This will remove it from AWS SES and reset to the default sender email. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={deletingIdentity}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteIdentity} 
            color="error" 
            variant="contained"
            disabled={deletingIdentity}
            startIcon={deletingIdentity ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deletingIdentity ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Configuration Form Component
interface ConfigurationFormProps {
  configuration: EmailConfig | null;
  onSave: (config: EmailConfig) => Promise<void>;
  onCancel: () => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ configuration, onSave, onCancel: _onCancel }) => {
  const [providerType, setProviderType] = useState<EmailProviderType>(configuration?.providerType || 'ses');
  const [formData, setFormData] = useState<EmailConfig>({
    providerType: configuration?.providerType || 'ses',
    ses: {
      defaultSenderEmail: configuration?.ses?.defaultSenderEmail || configuration?.awsSESDefaultSenderEmail || '',
      accessKey: (configuration as any)?.ses?.accessKey || (configuration as any)?.awsSESAccessKey || '',
      secretKey: (configuration as any)?.ses?.secretKey || (configuration as any)?.awsSESSecretKey || '',
      region: (configuration as any)?.ses?.region || (configuration as any)?.awsSESRegion || '',
      configSetName: (configuration as any)?.ses?.configSetName || (configuration as any)?.awsSESConfigSetNAme || '',
    },
    office365: (configuration as any)?.office365 || {
      smtp: {
        host: 'smtp.office365.com',
        user: '',
        fromAddress: '',
        port: 587,
        ssl: false,
        starttls: true,
        requiretls: true,
        debugEmail: false,
        oauthFlow: true,
      },
      oauth: {
        authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
        clientId: '',
        clientSecret: '',
        scopes: [
          'https://outlook.office.com/SMTP.Send',
          'https://outlook.office.com/Mail.Read',
          'https://outlook.office.com/Mail.Send',
          'https://outlook.office.com/User.Read',
          'email', 'openid', 'profile', 'offline_access'
        ],
        authCodeRedirectUrl: 'http://localhost',
        refreshUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
        refreshToken: '',
      }
    },
    testRecipientEmail: configuration?.testRecipientEmail || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Box component="form" id="email-configuration-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          SelectProps={{ native: true }}
          label="Mailing Server Type"
          value={providerType}
          onChange={(e) => {
            const val = (e.target.value as EmailProviderType);
            setProviderType(val);
            setFormData({ ...formData, providerType: val });
          }}
          sx={{ minWidth: 240 }}
        >
          <option value="ses">Amazon SES</option>
          <option value="office365">Office365</option>
        </TextField>
        <TextField
          label="Test Recipient Email"
          value={formData.testRecipientEmail || ''}
          onChange={(e) => setFormData({ ...formData, testRecipientEmail: e.target.value })}
          fullWidth
          margin="normal"
          variant="outlined"
          type="email"
          placeholder="test@example.com"
        />
      </Box>

      {providerType === 'ses' && (
        <>
          <TextField
            label="Default Sender Email"
            value={formData.ses?.defaultSenderEmail || ''}
            onChange={(e) => setFormData({ ...formData, ses: { ...(formData.ses || {}), defaultSenderEmail: e.target.value } })}
            fullWidth
            margin="normal"
            variant="outlined"
            type="email"
            placeholder="sender@yourdomain.com"
          />
          <TextField
            label="AWS Access Key"
            value={formData.ses?.accessKey || ''}
            onChange={(e) => setFormData({ ...formData, ses: { ...(formData.ses || {}), accessKey: e.target.value } })}
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="AKIA..."
          />
          <TextField
            label="AWS Secret Key"
            value={formData.ses?.secretKey || ''}
            onChange={(e) => setFormData({ ...formData, ses: { ...(formData.ses || {}), secretKey: e.target.value } })}
            fullWidth
            margin="normal"
            variant="outlined"
            type="password"
            placeholder="********"
          />
          <TextField
            label="Region"
            value={formData.ses?.region || ''}
            onChange={(e) => setFormData({ ...formData, ses: { ...(formData.ses || {}), region: e.target.value } })}
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="us-east-1"
          />
          <TextField
            label="Config Set Name"
            value={formData.ses?.configSetName || ''}
            onChange={(e) => setFormData({ ...formData, ses: { ...(formData.ses || {}), configSetName: e.target.value } })}
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="Optional"
          />
        </>
      )}

      {providerType === 'office365' && (
        <>
          <TextField label="SMTP_HOST" value={formData.office365?.smtp?.host || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), host: e.target.value } } })} fullWidth margin="normal" variant="outlined" placeholder="smtp.office365.com" />
          <TextField label="SMTP_USER" value={formData.office365?.smtp?.user || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), user: e.target.value } } })} fullWidth margin="normal" variant="outlined" placeholder="user@yourdomain.com" />
          <TextField label="SMTP_PASSWORD" value={formData.office365?.smtp?.password || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), password: e.target.value } } })} fullWidth margin="normal" variant="outlined" type="password" placeholder="********" />
          <TextField label="SMTP_PORT" value={formData.office365?.smtp?.port || 587} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), port: Number(e.target.value) || 587 } } })} fullWidth margin="normal" variant="outlined" type="number" />
          <TextField label="SMTP_FROM_ADDRESS" value={formData.office365?.smtp?.fromAddress || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), fromAddress: e.target.value } } })} fullWidth margin="normal" variant="outlined" type="email" placeholder="no-reply@yourdomain.com" />

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField label="SMTP_SSL" value={String(!!formData.office365?.smtp?.ssl)} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), ssl: e.target.value === 'true' } } })} select SelectProps={{ native: true }} sx={{ minWidth: 160, mt: 1.5 }}>
              <option value="false">false</option>
              <option value="true">true</option>
            </TextField>
            <TextField label="SMTP_START_TLS" value={String(!!formData.office365?.smtp?.starttls)} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), starttls: e.target.value === 'true' } } })} select SelectProps={{ native: true }} sx={{ minWidth: 160, mt: 1.5 }}>
              <option value="true">true</option>
              <option value="false">false</option>
            </TextField>
            <TextField label="SMTP_REQUIRE_TLS" value={String(formData.office365?.smtp?.requiretls !== false)} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), requiretls: e.target.value === 'true' } } })} select SelectProps={{ native: true }} sx={{ minWidth: 200, mt: 1.5 }}>
              <option value="true">true</option>
              <option value="false">false</option>
            </TextField>
            <TextField label="SMTP_DEBUG_EMAIL" value={String(!!formData.office365?.smtp?.debugEmail)} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), debugEmail: e.target.value === 'true' } } })} select SelectProps={{ native: true }} sx={{ minWidth: 200, mt: 1.5 }}>
              <option value="false">false</option>
              <option value="true">true</option>
            </TextField>
            <TextField label="SMTP_OAUTH_FLOW" value={String(!!formData.office365?.smtp?.oauthFlow)} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), smtp: { ...(formData.office365?.smtp || {}), oauthFlow: e.target.value === 'true' } } })} select SelectProps={{ native: true }} sx={{ minWidth: 200, mt: 1.5 }}>
              <option value="true">true</option>
              <option value="false">false</option>
            </TextField>
          </Box>

          <Divider sx={{ my: 2 }} />
          <TextField label="OAUTH_AUTH_URL" value={formData.office365?.oauth?.authUrl || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), authUrl: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
          <TextField label="OAUTH_TOKEN_URL" value={formData.office365?.oauth?.tokenUrl || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), tokenUrl: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
          <TextField label="OAUTH_CLIENT_ID" value={formData.office365?.oauth?.clientId || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), clientId: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
          <TextField label="OAUTH_CLIENT_SECRET" value={formData.office365?.oauth?.clientSecret || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), clientSecret: e.target.value } } })} fullWidth margin="normal" variant="outlined" type="password" />
          <TextField label="OAUTH_SCOPES (comma separated)" value={(formData.office365?.oauth?.scopes || []).join(', ')} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), scopes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } } })} fullWidth margin="normal" variant="outlined" placeholder="scope1, scope2" />
          <TextField label="OAUTH_AUTH_CODE_REDIRECT_URL" value={formData.office365?.oauth?.authCodeRedirectUrl || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), authCodeRedirectUrl: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
          <TextField label="OAUTH_REFRESH_URL" value={formData.office365?.oauth?.refreshUrl || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), refreshUrl: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
          <TextField label="OAUTH_REFRESH_TOKEN" value={formData.office365?.oauth?.refreshToken || ''} onChange={(e) => setFormData({ ...formData, office365: { ...(formData.office365 || {}), oauth: { ...(formData.office365?.oauth || {}), refreshToken: e.target.value } } })} fullWidth margin="normal" variant="outlined" />
        </>
      )}
    </Box>
  );
};
